import {GitHubAvatar} from '@github-ui/github-avatar'
import {XIcon} from '@primer/octicons-react'
import {Heading, IconButton} from '@primer/react'

import {anchorComment} from '../../helpers/anchor-helpers'
import type {AddCommentEditorProps} from './AddCommentEditor'
import {AddCommentEditor} from './AddCommentEditor'

export interface StartThreadCommentProps
  extends Pick<
    AddCommentEditorProps,
    | 'batchingEnabled'
    | 'fileLevelComment'
    | 'filePath'
    | 'focusOnMount'
    | 'lineNumber'
    | 'startLineNumber'
    | 'subjectId'
    | 'suggestedChangesConfig'
  > {
  addCommentDialogTitle?: string
  isLeftSide: boolean | undefined
  onAddComment: (args: {
    onCompleted?: (threadId: string, commentDatabaseId: number | undefined | null) => void
    onError: (error: Error) => void
    submitBatch?: boolean | undefined
    text: string
  }) => void
  onClose?: () => void
  viewerData: {
    avatarUrl: string
    login: string
  }
  showOnCloseIcon?: boolean
  threadsConnectionId?: string
}

/**
 * The StartThreadComment component is used to render the comment editor when starting a new inline thread.
 */
export function StartThreadComment({
  addCommentDialogTitle,
  isLeftSide,
  fileLevelComment,
  filePath,
  focusOnMount = true,
  lineNumber,
  onAddComment,
  onClose,
  viewerData,
  threadsConnectionId,
  showOnCloseIcon = true,
  ...rest
}: StartThreadCommentProps) {
  const handleAddComment = ({
    commentText,
    onCompleted,
    onError,
    submitBatch,
  }: {
    commentText: string
    onCompleted?: (threadId: string, commentDatabaseId?: number | undefined | null) => void
    onError: (error: Error) => void
    submitBatch?: boolean
  }) => {
    const handleCompleted = (threadId: string, commentDatabaseId: number | undefined | null) => {
      if (commentDatabaseId) anchorComment(commentDatabaseId.toString())
      onCompleted?.(threadId, commentDatabaseId)
    }

    onAddComment({
      text: commentText,
      onError,
      onCompleted: handleCompleted,
      submitBatch,
    })
  }

  return (
    <div className="px-2 pb-2 pt-1">
      <div className="d-flex flex-justify-between flex-items-center">
        <Heading as="h4" className="f5 ml-1 pt-1 pb-2">
          <GitHubAvatar alt={viewerData.login} size={24} src={viewerData.avatarUrl || ''} className="mr-2" />
          {fileLevelComment ? <>Add comment on file</> : <span>{addCommentDialogTitle ?? 'Add a comment'}</span>}
        </Heading>
        {onClose && showOnCloseIcon && (
          <IconButton variant="invisible" icon={XIcon} onClick={onClose} aria-label="Cancel" />
        )}
      </div>
      <AddCommentEditor
        focusOnMount={focusOnMount}
        condensed={false}
        fileLevelComment={fileLevelComment ?? false}
        filePath={filePath}
        lineNumber={lineNumber}
        onAddComment={handleAddComment}
        onCancelComment={onClose}
        side={isLeftSide ? 'LEFT' : 'RIGHT'}
        {...rest}
      />
    </div>
  )
}

try{ StartThreadComment.displayName ||= 'StartThreadComment' } catch {}